import { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import orderService from '../../api/services/order.service';
import { useCart } from '../../context/CartContext';
import './PaymentSuccess.css';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchCart } = useCart();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [orderDetails, setOrderDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const verificationAttempted = useRef(false);

  // Capture identifiers once on mount to ensure stability
  const { orderUuid, stripeSessionId, razorpayPaymentId, razorpayOrderId, razorpaySignature, paypalOrderId } = useMemo(() => {
    const fromUrl = searchParams.get('order_uuid');
    const fromStorage = localStorage.getItem('pending_order_uuid');
    const sessionId = searchParams.get('session_id');
    const rzpPaymentId = searchParams.get('razorpay_payment_id');
    const ppToken = searchParams.get('token');
    const ppOrderIdFromQuery = searchParams.get('paypal_order_id');
    const storedPaypalOrderId = localStorage.getItem('pending_paypal_order_id');
    const resolvedPaypalOrderId = ppOrderIdFromQuery || storedPaypalOrderId || ppToken;
    
    // Check sessionStorage for a cached order_uuid for this payment session
    const sessionKey = `verified_order_${sessionId || rzpPaymentId || resolvedPaypalOrderId}`;
    const fromSession = (sessionId || rzpPaymentId || resolvedPaypalOrderId) ? sessionStorage.getItem(sessionKey) : null;

    return {
      orderUuid: fromUrl || fromSession || fromStorage,
      stripeSessionId: sessionId,
      razorpayPaymentId: rzpPaymentId,
      razorpayOrderId: searchParams.get('razorpay_order_id'),
      razorpaySignature: searchParams.get('razorpay_signature'),
      paypalOrderId: resolvedPaypalOrderId
    };
  }, [searchParams]);

  useEffect(() => {
    // Prevent multiple verification attempts (especially in Strict Mode)
    // However, if we don't have orderDetails yet (e.g. on refresh), we should allow it
    if (verificationAttempted.current && orderDetails) return;
    
    let isMounted = true;
    const verify = async () => {
      // Allow verification if we have at least one valid identifier
      if (!orderUuid && !stripeSessionId && !razorpayPaymentId && !paypalOrderId) {
        if (isMounted) {
          setStatus('error');
          setErrorMessage('Missing payment identifiers. Please contact support.');
        }
        return;
      }

      verificationAttempted.current = true;

      try {
        const payload = {};
        
        // Prioritize stripe_session_id
        if (stripeSessionId) {
          payload.stripe_session_id = stripeSessionId;
        } 
        // Then Razorpay parameters
        else if (razorpayPaymentId) {
          payload.razorpay_payment_id = razorpayPaymentId;
          if (razorpayOrderId) payload.razorpay_order_id = razorpayOrderId;
          if (razorpaySignature) payload.razorpay_signature = razorpaySignature;
        }
        // Then PayPal parameters
        else if (paypalOrderId) {
          payload.paypal_order_id = paypalOrderId;
          // console.log("[PaymentSuccess] PayPal order ID:", paypalOrderId);
        }


        const response = await orderService.verifyPayment(payload);

        if (isMounted) {
          if (response.status) {
            setStatus('success');
            setOrderDetails(response.data);
            
            // Refresh cart after successful verification to clear the cart in the frontend
            fetchCart().catch(err => console.error("Failed to refresh cart after success:", err));
            
            // Cache the order_uuid in sessionStorage to survive refreshes/back navigation
            const finalOrderUuid = response.data?.order_uuid || 
                                 (response.data?.order_uuids && response.data.order_uuids.length > 0 ? response.data.order_uuids[0] : null) || 
                                 orderUuid;
            if (finalOrderUuid && (stripeSessionId || razorpayPaymentId || paypalOrderId)) {
              const sessionKey = `verified_order_${stripeSessionId || razorpayPaymentId || paypalOrderId}`;
              sessionStorage.setItem(sessionKey, finalOrderUuid);
            }
          } else {
            setStatus('error');
            setErrorMessage(response.message || 'Payment verification failed.');
          }
          // Clear pending order_uuid from localStorage after attempt
          localStorage.removeItem('pending_order_uuid');
          localStorage.removeItem('pending_paypal_order_id');
        }
      } catch (err) {
        if (isMounted) {
          setStatus('error');
          setErrorMessage(err.message || 'An error occurred during verification.');
          // Clear pending order_uuid from localStorage after attempt
          localStorage.removeItem('pending_order_uuid');
          localStorage.removeItem('pending_paypal_order_id');
        }
      }
    };

    verify();
    return () => { isMounted = false; };
  }, [orderUuid, stripeSessionId, razorpayPaymentId, razorpayOrderId, razorpaySignature, paypalOrderId, orderDetails]);

  return (
    <div className="payment-success-container">
      <div className="success-card">
        {status === 'verifying' && (
          <div className="verifying-state">
            <div className="spinner"></div>
            <h2>Finalizing Your Order...</h2>
            <p>Please wait while we confirm your payment details. Do not refresh or close this window.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="success-state">
            <div className="success-icon">✓</div>
            <h1>Order Successfully Placed!</h1>
            <p className="thank-you">Thank you for shopping with us. We've received your order and are preparing it for shipment.</p>
            <div className="order-info">
              <p>Order Reference: <strong>{orderDetails?.order_uuid || (orderDetails?.order_uuids && orderDetails.order_uuids[0]) || orderUuid}</strong></p>
              <p>Payment Status: <span className="status-badge paid">PAID</span></p>
            </div>
            <div className="action-buttons">
              <Link to="/orders" className="view-orders-btn">View My Orders</Link>
              <Link to="/" className="continue-shopping-btn">Continue Shopping</Link>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="error-state">
            <div className="error-icon">!</div>
            <h1>Payment Status Unknown</h1>
            <p className="error-msg">We're having trouble confirming your payment status at this moment. If any amount was deducted from your account, please don't worry—our team will verify and update your order within 24 hours.</p>
            <div className="action-buttons">
              <Link to="/contact" className="contact-btn">Contact Support</Link>
              <Link to="/" className="home-btn">Go to Home</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



